import React, { useState, useEffect } from 'react';

import {
  UsersIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BanknotesIcon,
  ArrowPathIcon,  // ← AJOUTÉ
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import clientService from '../services/clientService';
import loanService from '../services/loanService';
import accountService from '../services/accountService';
import repaymentService from '../services/repaymentService';
import { useToast } from '../contexts/ToastContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalClients: 0,
    totalSavings: 0,
    totalLoans: 0,
    activeLoans: 0,
    totalRepayments: 0,
    overdueLoans: 0,
    monthlyGrowth: 0,
    clientGrowth: 0,
    collectionRate: 0,
  });
  
  const [loading, setLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loanPerformance, setLoanPerformance] = useState([]);
  const [savingsTrend, setSavingsTrend] = useState([]);
  const [portfolioByProduct, setPortfolioByProduct] = useState([]);
  const { showError } = useToast();
  const { formatCurrency } = useCurrency();

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all statistics in parallel
      const [
        clientsRes,
        accountsRes,
        loansRes,
        repaymentsRes,
        overdueRes,
        dueRepaymentsRes,
        allRepaymentsRes,
      ] = await Promise.allSettled([
        clientService.getClientStatistics(),
        accountService.getAccountSummary(),
        loanService.getLoanStatistics(),
        repaymentService.getRepaymentSummary(),
        loanService.getOverdueLoans(),
        repaymentService.getDueRepayments(new Date().toISOString().slice(0, 10)),
        repaymentService.getAllRepayments(),
      ]);

      // Process responses
      const clientStats = clientsRes.status === 'fulfilled' ? clientsRes.value : {};
      const accountStats = accountsRes.status === 'fulfilled' ? accountsRes.value : {};
      const loanStats = loansRes.status === 'fulfilled' ? loansRes.value : {};
      const repaymentStats = repaymentsRes.status === 'fulfilled' ? repaymentsRes.value : {};
      const overdueLoans = overdueRes.status === 'fulfilled' ? overdueRes.value : [];
      const dueRepayments = dueRepaymentsRes.status === 'fulfilled' ? dueRepaymentsRes.value : [];
      const allRepayments = allRepaymentsRes.status === 'fulfilled' ? allRepaymentsRes.value : [];

      const nextStats = {
        totalClients: clientStats.total || 0,
        totalSavings: accountStats.total_balance || 0,
        totalLoans: loanStats.total_amount || 0,
        activeLoans: loanStats.active_count || loanStats.active_loans || 0,
        totalRepayments: repaymentStats.total_collected || 0,
        overdueLoans: overdueLoans.length || 0,
        monthlyGrowth: loanStats.monthly_growth,
        clientGrowth: clientStats.monthly_growth || 0,
        collectionRate: repaymentStats.collection_rate || 0,
      };
      setStats(nextStats);
      setPortfolioByProduct(loanStats.portfolio_by_product || []);
      buildDashboardCharts(accountStats, loanStats, repaymentStats, overdueLoans, dueRepayments, allRepayments);
      
    } catch (error) {
      showError('Erreur lors du chargement des données du tableau de bord');
      console.error('Dashboard error:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildDashboardCharts = (accountStats, loanStats, repaymentStats, overdueLoans, dueRepayments, allRepayments) => {
    setLoanPerformance([
      { month: 'En attente', approved: loanStats.pending_loans || 0, disbursed: 0 },
      { month: 'Approuvés', approved: loanStats.approved_loans || 0, disbursed: 0 },
      { month: 'Actifs', approved: loanStats.active_loans || 0, disbursed: loanStats.total_amount || 0 },
      { month: 'Terminés', approved: loanStats.completed_loans || 0, disbursed: loanStats.total_collected || 0 },
      { month: 'Défauts', approved: loanStats.defaulted_loans || 0, disbursed: loanStats.total_due || 0 },
    ]);

    const byAccountType = accountStats.by_account_type || {};
    const savingsData = Object.keys(byAccountType).length
      ? Object.entries(byAccountType).map(([type, item]) => ({ date: type, balance: item.balance || 0 }))
      : [{ date: 'Solde actuel', balance: accountStats.total_balance || 0 }];
    setSavingsTrend(savingsData);

    const repaymentActivities = allRepayments
      .filter((repayment) => repayment.amount_paid > 0)
      .slice(0, 3)
      .map((repayment) => ({
        id: `repayment-${repayment.id}`,
        type: 'repayment',
        description: 'Remboursement enregistré',
        client: repayment.client_name || repayment.loan_number || '',
        amount: repayment.amount_paid || 0,
        date: repayment.payment_date || repayment.created_at ? new Date(repayment.payment_date || repayment.created_at) : null,
      }));
    const dueActivities = dueRepayments.slice(0, 2).map((repayment) => ({
      id: `due-${repayment.id}`,
      type: 'loan',
      description: 'Échéance à traiter',
      client: repayment.client_name || repayment.loan_number || '',
      amount: (repayment.total_amount || 0) - (repayment.amount_paid || 0),
      date: repayment.due_date ? new Date(repayment.due_date) : null,
    }));
    const overdueActivities = overdueLoans.slice(0, 2).map((loan) => ({
      id: `overdue-${loan.id}`,
      type: 'loan',
      description: 'Prêt en retard',
      client: loan.client_name || loan.loan_number || '',
      amount: loan.amount_due || loan.amount || 0,
      date: loan.end_date ? new Date(loan.end_date) : null,
    }));
    setRecentActivity([...repaymentActivities, ...dueActivities, ...overdueActivities].slice(0, 5));
  };

  const StatCard = ({ title, value, icon: Icon, trend, subtitle, color = 'primary' }) => {
    const colorClasses = {
      primary: 'bg-primary-50 text-primary-600',
      success: 'bg-green-50 text-green-600',
      warning: 'bg-yellow-50 text-yellow-600',
      danger: 'bg-red-50 text-red-600',
    };

    return (
      <div className="card">
        <div className="p-6">
          <div className="flex items-center">
            <div className={`flex-shrink-0 rounded-md p-3 ${colorClasses[color]}`}>
              <Icon className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{title}</p>
              <p className="text-2xl font-semibold text-gray-900">
                {typeof value === 'number' ? value.toLocaleString('fr-FR') : value}
              </p>
              {trend !== undefined && trend !== null && (
                <div className="flex items-center mt-1">
                  {trend > 0 ? (
                    <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />
                  ) : (
                    <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />
                  )}
                  <span className={`text-sm ml-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {trend > 0 ? '+' : ''}{trend}%
                  </span>
                  {subtitle && (
                    <span className="text-sm text-gray-500 ml-2">{subtitle}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-sm text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Tableau de bord</h1>
        <p className="mt-1 text-sm text-gray-600">
          {format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Clients"
          value={stats.totalClients}
          icon={UsersIcon}
          trend={stats.clientGrowth}
          subtitle="ce mois"
        />
        <StatCard
          title="Épargne Totale"
          value={formatCurrency(stats.totalSavings)}
          icon={CreditCardIcon}
          subtitle="Solde réel des comptes"
          color="success"
        />
        <StatCard
          title="Portefeuille Prêts"
          value={formatCurrency(stats.totalLoans)}
          icon={CurrencyDollarIcon}
          trend={stats.monthlyGrowth}
          subtitle={`${stats.activeLoans} prêts actifs`}
          color="warning"
        />
        <StatCard
          title="Taux de Recouvrement"
          value={`${stats.collectionRate}%`}
          icon={BanknotesIcon}
          subtitle={`${stats.overdueLoans} prêts en retard`}
          color={stats.collectionRate > 90 ? 'success' : 'danger'}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Loan Performance Chart */}
        <div className="card">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Performance des Prêts</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={loanPerformance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => [formatCurrency(value), 'Montant']} />
                  <Legend />
                  <Bar dataKey="approved" name="Approuvés" fill="#3b82f6" />
                  <Bar dataKey="disbursed" name="Décaissés" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Savings Trend Chart */}
        <div className="card">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Évolution de l'Épargne</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={savingsTrend}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip formatter={(value) => [formatCurrency(value), 'Solde']} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="balance"
                    name="Solde Total"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity and Loan Portfolio */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Recent Activity */}
        <div className="card lg:col-span-2">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Activité Récente</h3>
            <div className="flow-root">
              <ul className="-mb-8">
                {recentActivity.map((activity, activityIdx) => (
                  <li key={activity.id}>
                    <div className="relative pb-8">
                      {activityIdx !== recentActivity.length - 1 ? (
                        <span
                          className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                          aria-hidden="true"
                        />
                      ) : null}
                      <div className="relative flex space-x-3">
                        <div>
                          <span className={`
                            h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white
                            ${activity.type === 'loan' ? 'bg-blue-500' : ''}
                            ${activity.type === 'deposit' ? 'bg-green-500' : ''}
                            ${activity.type === 'repayment' ? 'bg-purple-500' : ''}
                            ${activity.type === 'account' ? 'bg-yellow-500' : ''}
                          `}>
                            {activity.type === 'loan' && <CurrencyDollarIcon className="h-4 w-4 text-white" />}
                            {activity.type === 'deposit' && <ArrowTrendingUpIcon className="h-4 w-4 text-white" />}
                            {activity.type === 'repayment' && <ArrowPathIcon className="h-4 w-4 text-white" />}
                            {activity.type === 'account' && <CreditCardIcon className="h-4 w-4 text-white" />}
                          </span>
                        </div>
                        <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                          <div>
                            <p className="text-sm text-gray-900">{activity.description}</p>
                            <p className="text-sm text-gray-500">{activity.client}</p>
                          </div>
                          <div className="whitespace-nowrap text-right text-sm text-gray-500">
                            <div className="font-medium text-gray-900">
                              {formatCurrency(activity.amount)}
                            </div>
                            <div>{activity.date ? format(activity.date, 'dd/MM HH:mm') : '—'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Loan Portfolio Distribution */}
        <div className="card">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Portefeuille par Produit</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={portfolioByProduct}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {portfolioByProduct.map((item, index) => (
                      <Cell key={`cell-${item.product_id}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name, item) => [formatCurrency(value), `${item.payload.loan_count} prêt(s)`]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {!portfolioByProduct.length && <p className="text-center text-sm text-gray-500">Aucun encours de prêt à afficher.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
